import React, { useEffect } from 'react';
import { ChevronRight, TrendingUp, Lightbulb, Mail, MapPin, Github, Linkedin, BookOpen, Users, MessageCircle } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

function Home() {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        // Add smooth scroll behavior
        document.documentElement.style.scrollBehavior = 'smooth';
        return () => {
            // Cleanup when component unmounts
            document.documentElement.style.scrollBehavior = '';
        };
    }, []);

    // Handle scroll from navigation state
    useEffect(() => {
        const scrollTo = location.state?.scrollTo;
        if (scrollTo) {
            // Small delay to ensure DOM is ready
            setTimeout(() => {
                const element = document.getElementById(scrollTo);
                if (element) {
                    const navbarHeight = 80;
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    });
                }
                // Clear the state to prevent re-scroll on refresh
                window.history.replaceState({}, document.title);
            }, 200);
        }
    }, [location]);

    const styles = `
        .hero-title {
            font-size: 3.2rem;
            font-weight: 800;
            background: linear-gradient(90deg, #6366f1, #06b6d4 60%, #3b82f6 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            margin-bottom: 1.5rem;
            letter-spacing: 1px;
        }
        .cta-btn {
            border: none;
            border-radius: 2rem;
            font-size: 1.18rem;
            font-weight: 600;
            padding: 1.1rem 0;
            box-shadow: 0 6px 32px 0 rgba(79,70,229,0.10);
            letter-spacing: 0.5px;
            background: white;
            color: #ffffffff;
            position: relative;
            overflow: hidden;
            transition: box-shadow 0.18s, transform 0.18s;
        }
        .cta-btn.cta-primary {
            background-color: #0d53bdff;
            color: #fff;
        }
        .cta-btn.cta-secondary {
            background-color: #125ed1ff;
            color: #fff;
        }
        .cta-btn .cta-icon {
            background: rgba(147, 166, 236, 0.3);
            border-radius: 50%;
            padding: 0.48rem;
            display: flex;
            align-items: center;
            margin: 0 0.5rem;
            font-size: 1.2rem;
        }
        .cta-btn:focus {
            outline: 2px solid #06b6d4;
        }
        .cta-btn:hover, .cta-btn:active {
            background-color: #0a326dff;
            color: #fff !important;
            transition: background-color 1s;
            box-shadow: 0 12px 32px 0 rgba(6,182,212,0.13);
            z-index: 2;
        }
        .cta-btn:active {
            transform: translateY(0) scale(0.99);
            color: #fff !important;
            background-color: #0a326dff !important;
        }
        .card-glass {
            background: rgba(255, 255, 255, 0.13);
            backdrop-filter: blur(12px);
            border-radius: 1.2rem;
            box-shadow: 0 4px 32px rgba(79,70,229,0.07);
            border: 1px solid rgba(99,102,241,0.13);
            transition: box-shadow 0.2s, transform 0.2s;
        }
        .card-glass:hover {
            box-shadow: 0 8px 36px 0 rgba(6,182,212,0.13);
            border-color: #06b6d4;
        }
        .idea-icon {
            font-size: 2.5rem;
            margin-bottom: 0.7rem;
        }
        
        .section-title {
            font-size: 2rem;
            font-weight: 700;
            background: linear-gradient(90deg, #6366f1, #06b6d4);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            margin-bottom: 2rem;
        }
        
        .contact-icon {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #101010 !important;
            border: 1px solid #0606062b;
            margin-right: 1rem;
        }
        
        .social-icon1 {
            width: 45px;
            height: 45px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #101010 !important;
            transition: all 0.3s ease;
            cursor: pointer;
            border: 1px solid rgba(99, 102, 241, 0.2);
        }
        
        .social-icon1:hover {
            background-color: #124be8;
            color: white !important;
            box-shadow: 0 8px 20px rgba(99, 102, 241, 0.3);
        }

        .github:hover{
            background-color: #0b0b0b;
            color: white !important;
            box-shadow: 0 8px 20px rgba(99, 102, 241, 0.3);
        }

        .step-number {
            width: 35px;
            height: 35px;
            background: linear-gradient(135deg, #6366f1, #06b6d4);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: 700;
            margin-right: 1rem;
            flex-shrink: 0;
        }

        .full-width-section {
            width: 100%;
            max-width: 100%;
        }
        
        /* Tablet Devices */
        @media (max-width: 1024px) {
            .hero-title { 
                font-size: 2.8rem; 
            }
            .section-title {
                font-size: 1.8rem;
            }
        }
        
        /* Mobile Devices */
        @media (max-width: 767px) {
            .hero-title { 
                font-size: 2.1rem; 
                margin-bottom: 1rem;
            }
            .cta-btn { 
                font-size: 1rem; 
                padding: 0.9rem 0; 
            }
            .cta-btn .cta-icon {
                padding: 0.4rem;
                margin: 0 0.4rem;
            }
            .section-title {
                font-size: 1.6rem;
                margin-bottom: 1.5rem;
            }
            .contact-icon {
                width: 35px;
                height: 35px;
                margin-right: 0.8rem;
            }
            .step-number {
                width: 30px;
                height: 30px;
                font-size: 0.9rem;
            }
            .social-icon1{
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                background-color: #124be8 !important;
                color: white !important;
            }
            .github{
                background-color: #0b0b0b !important;
            }
        }
        
        /* Small Mobile Devices */
        @media (max-width: 576px) {
            .hero-title { 
                font-size: 1.8rem; 
                margin-bottom: 0.8rem;
            }
            .cta-btn { 
                font-size: 0.95rem; 
                padding: 0.85rem 0; 
            }
            .cta-btn .cta-icon {
                padding: 0.35rem;
                margin: 0 0.3rem;
            }
            .cta-btn .cta-icon svg {
                width: 18px;
                height: 18px;
            }
            .section-title {
                font-size: 1.4rem;
            }
            .social-icon1 {
                width: 40px;
                height: 40px;
            }
            .step-number {
                width: 28px;
                height: 28px;
                font-size: 0.85rem;
                margin-right: 0.75rem;
            }
        }
        
        /* Extra Small Mobile Devices */
        @media (max-width: 375px) {
            .hero-title { 
                font-size: 1.6rem; 
            }
            .cta-btn { 
                font-size: 0.9rem; 
                padding: 0.8rem 0; 
            }
            .section-title {
                font-size: 1.3rem;
            }
        }
    `;

    return (
        <div>
            <style>{styles}</style>
            <div style={{ minHeight: '100vh', background: 'linear-gradient(120deg, #f0f9ff 0%, #e0e7ff 100%)', position: 'relative', overflow: 'hidden' }}>
                <div className="container py-5" style={{ position: 'relative', zIndex: 10 }}>
                    <div className="row align-items-center justify-content-center py-2">
                        <div className="col-12 text-center mx-auto full-width-section">
                            {/* Hero Title */}
                            <h1 className="hero-title">AI Business Architect</h1>
                            {/* Subtitle */}
                            <h2 className="h3 mb-2" style={{ color: '#222', fontWeight: 400 }}>
                                <b>Turn any idea into a real business with AI.</b>
                            </h2>
                            <p className="lead mb-5" style={{ color: '#444', fontSize: '1.13rem' }}>
                                Get AI-powered strategies, market analysis, and launch insights in minutes.
                            </p>
                            {/* CTA Buttons */}
                            <div className="row justify-content-center mb-5 g-3">
                                <div className="col-12 col-md-5 mb-3 mb-md-0">
                                    <button
                                        onClick={() => {
                                            navigate('/new');
                                        }}
                                        className="btn cta-btn cta-primary w-100 d-flex align-items-center justify-content-center shadow-lg"
                                    >
                                        <span className="cta-icon">
                                            <Lightbulb size={22} />
                                        </span>
                                        Start with New Idea
                                        <span className="cta-icon">
                                            <ChevronRight size={20} />
                                        </span>
                                    </button>
                                </div>
                                <div className="col-12 col-md-5">
                                    <button
                                        onClick={() => {
                                            navigate('/existing');
                                        }}
                                        className="btn cta-btn cta-secondary w-100 d-flex align-items-center justify-content-center shadow-lg"
                                    >
                                        <span className="cta-icon">
                                            <TrendingUp size={22} />
                                        </span>
                                        Analyze Existing Business
                                        <span className="cta-icon">
                                            <ChevronRight size={20} />
                                        </span>
                                    </button>
                                </div>
                            </div>
                            {/* Idea Cards */}
                            <div className="row mt-5 g-3 justify-content-center">
                                {[
                                    { title: 'Food Startup', desc: 'Farm-to-table meal delivery', icon: '🍽️' },
                                    { title: 'AI Tool SaaS', desc: 'Automation for small teams', icon: '⚡' },
                                    { title: 'Local Service', desc: 'Niche service business', icon: '🏪' },
                                ].map((idea, idx) => (
                                    <div key={idx} className="col-12 col-md-4 mb-3">
                                        <div className="card card-glass p-4 h-100" style={{ cursor: 'pointer' }}>
                                            <div className="idea-icon">{idea.icon}</div>
                                            <h5 className="card-title fw-bold mb-2">{idea.title}</h5>
                                            <p className="card-text" style={{ color: '#555', fontSize: '0.98rem' }}>
                                                {idea.desc}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* ───────────────────────────────────────────────────────────────── */}
                            {/* HOW TO USE SECTION */}
                            {/* ───────────────────────────────────────────────────────────────── */}
                            <div id="how-to-use" className="row mt-5 pt-4">
                                <div className="col-12">
                                    <h2 className="section-title">
                                        <BookOpen size={28} className="me-2" style={{ display: 'inline', verticalAlign: 'middle' }} />
                                        How to Use
                                    </h2>
                                </div>
                            </div>
                            <div className="row justify-content-center">
                                <div className="col-12">
                                    <div className="card card-glass p-4 p-md-5">
                                        <div className="row g-4">
                                            <div className="col-12 col-md-4">
                                                <div className="d-flex align-items-start">
                                                    <div className="step-number">1</div>
                                                    <div>
                                                        <h5 style={{ color: '#0d53bd', fontWeight: '600', marginBottom: '8px' }}>
                                                            Choose Your Path
                                                        </h5>
                                                        <p style={{ color: '#555', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                            Select "Start with New Idea" for fresh concepts or "Analyze Existing Business"
                                                            for current ventures.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <div className="d-flex align-items-start">
                                                    <div className="step-number">2</div>
                                                    <div>
                                                        <h5 style={{ color: '#0d53bd', fontWeight: '600', marginBottom: '8px' }}>
                                                            Fill the Form
                                                        </h5>
                                                        <p style={{ color: '#555', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                            Provide basic details about your business or idea. The more specific,
                                                            the better the AI analysis.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <div className="d-flex align-items-start">
                                                    <div className="step-number">3</div>
                                                    <div>
                                                        <h5 style={{ color: '#0d53bd', fontWeight: '600', marginBottom: '8px' }}>
                                                            Get AI Analysis
                                                        </h5>
                                                        <p style={{ color: '#555', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                            Receive comprehensive 12-section report covering market, strategy,
                                                            financials, and growth roadmap.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row mt-4">
                                            <div className="col-12">
                                                <div className="d-flex align-items-start">
                                                    <div className="step-number">4</div>
                                                    <div>
                                                        <h5 style={{ color: '#0d53bd', fontWeight: '600', marginBottom: '8px' }}>
                                                            Download & Implement
                                                        </h5>
                                                        <p style={{ color: '#555', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                                            Download full PDF report and start implementing actionable strategies
                                                            for your business growth.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ───────────────────────────────────────────────────────────────── */}
                            {/* ABOUT US SECTION */}
                            {/* ───────────────────────────────────────────────────────────────── */}
                            <div id="about" className="row mt-5 pt-4">
                                <div className="col-12">
                                    <h2 className="section-title">
                                        <Users size={28} className="me-2" style={{ display: 'inline', verticalAlign: 'middle' }} />
                                        About Us
                                    </h2>
                                </div>
                            </div>
                            <div className="row justify-content-start">
                                <div className="col-12">
                                    <div className="card card-glass p-4 p-md-5">
                                        <p className="mb-3" style={{ fontSize: '1.1rem', color: '#333', lineHeight: '1.8', textAlign: 'left' }}>
                                            <strong style={{ color: '#0d53bd' }}>InsightDraft AI</strong> is your intelligent co-founder,
                                            designed to help entrepreneurs, founders, and business owners turn ideas into actionable plans.
                                        </p>
                                        <p className="mb-3" style={{ fontSize: '1rem', color: '#555', lineHeight: '1.8', textAlign: 'left' }}>
                                            We combine advanced AI with real-world business frameworks to deliver comprehensive analysis
                                            covering market research, financial planning, growth strategy, and operational insights —
                                            all tailored to your unique business context.
                                        </p>
                                        <p className="mb-0" style={{ fontSize: '1rem', color: '#555', lineHeight: '1.8', textAlign: 'left' }}>
                                            Whether you're validating a new idea or scaling an existing business, our platform provides
                                            the clarity and confidence you need to make informed decisions.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* ───────────────────────────────────────────────────────────────── */}
                            {/* CONTACT SECTION */}
                            {/* ───────────────────────────────────────────────────────────────── */}
                            <div id="contact" className="row mt-5 pt-4">
                                <div className="col-12">
                                    <h2 className="section-title">
                                        <MessageCircle size={28} className="me-2" style={{ display: 'inline', verticalAlign: 'middle' }} />
                                        Get In Touch
                                    </h2>
                                </div>
                            </div>
                            <div className="row justify-content-center g-4">
                                <div className="col-12">
                                    <div className="card card-glass p-4 p-md-5">
                                        <div className="row g-4 align-items-center">
                                            {/* Contact Info */}
                                            <div className="col-12 col-md-7">
                                                <h4 className="mb-4" style={{ color: '#0d53bd', fontWeight: '600' }}>
                                                    Let's Connect
                                                </h4>

                                                <div className="d-flex align-items-center mb-4">
                                                    <div className="contact-icon">
                                                        <Mail size={20} />
                                                    </div>
                                                    <div>
                                                        <p className="mb-0" style={{ fontSize: '0.9rem', color: '#666' }}>Email Us</p>
                                                        <a href="mailto:c84289798@gmail.com" style={{ color: '#0d53bd', textDecoration: 'none', fontWeight: '500' }}>
                                                            c84289798@gmail.com
                                                        </a>
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center">
                                                    <div className="contact-icon">
                                                        <MapPin size={20} />
                                                    </div>
                                                    <div>
                                                        <p className="mb-0" style={{ fontSize: '0.9rem', color: '#666' }}>Location</p>
                                                        <p className="mb-0" style={{ color: '#333', fontWeight: '500' }}>
                                                            Ahmedabad, India
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Social Links */}
                                            <div className="col-12 col-md-5 follow-us-section">
                                                <h4 className="mb-4" style={{ color: '#0d53bd', fontWeight: '600' }}>
                                                    Follow Us
                                                </h4>
                                                <p className="mb-4" style={{ color: '#555', fontSize: '0.95rem' }}>
                                                    Stay updated with the latest features and business insights.
                                                </p>
                                                <div className="d-flex gap-3 justify-content-center justify-content-md-start">
                                                    <a
                                                        href="https://www.linkedin.com/in/aman-rajput-2b7680316/"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="social-icon1"
                                                        style={{ textDecoration: 'none' }}
                                                    >
                                                        <Linkedin size={22} />
                                                    </a>
                                                    <a
                                                        href="https://github.com/rajput81325"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="social-icon1 github"
                                                        style={{ textDecoration: 'none' }}
                                                    >
                                                        <Github size={22} />
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ───────────────────────────────────────────────────────────────── */}
                            {/* FOOTER */}
                            {/* ───────────────────────────────────────────────────────────────── */}
                            <div className="row mt-5 pt-4">
                                <div className="col-12">
                                    <hr style={{ borderColor: 'rgba(99,102,241,0.2)' }} />
                                    <p className="mt-3" style={{ color: '#666', fontSize: '0.9rem' }}>
                                        © {new Date().getFullYear()} InsightDraft AI. All rights reserved.
                                    </p>
                                    <p style={{ color: '#888', fontSize: '0.85rem' }}>
                                        Made with ❤️ for entrepreneurs everywhere
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Home;